import { Broadcast, BroadcastDisabled, BroadcastError } from '@clearkit/icons';
import classnames from 'classnames';
import { CKSkeleton } from 'clearkit';

import {
  ActiveIntegrationCardAccountConnectorFragment,
  FieldMapping,
  Maybe,
} from '~/generated/graphql';

const successColor = 'green-500';
const dangerColor = 'red-500';
const idleColor = 'gray-500';

type IntegrationStatusProps = {
  accountConnector: ActiveIntegrationCardAccountConnectorFragment;
  subIntegrationsData: FieldMapping[];
  subIntegrationsLoaded: boolean;
};

type SyncData = {
  syncStatus: Maybe<string> | undefined;
  isConfigured: boolean;
  isLoading?: boolean;
};

function statusText({ syncStatus, isConfigured }: SyncData): string {
  if (syncStatus === 'error') {
    return 'Error';
  } else if (isConfigured) {
    return 'Enrichment on';
  } else {
    return 'Enrichment off';
  }
}

function statusColor({ syncStatus, isConfigured }: SyncData): string {
  if (syncStatus === 'error') {
    return dangerColor;
  } else if (isConfigured) {
    return successColor;
  } else {
    return idleColor;
  }
}

function StatusIcon({ syncStatus, isConfigured, isLoading }: SyncData) {
  const iconSize = 18;
  const iconProps = { height: iconSize, width: iconSize };

  return (
    <CKSkeleton className="w-5 h-5" isLoading={isLoading} label="loading">
      {syncStatus === 'error' ? (
        <BroadcastError {...iconProps} />
      ) : isConfigured ? (
        <Broadcast {...iconProps} />
      ) : (
        <BroadcastDisabled {...iconProps} />
      )}
    </CKSkeleton>
  );
}

function IntegrationStatus({
  accountConnector,
  subIntegrationsData,
  subIntegrationsLoaded,
}: IntegrationStatusProps) {
  const syncStatus = accountConnector.syncStatus;
  const isLoading = !subIntegrationsLoaded;
  const isConfigured = subIntegrationsData.some((data) => data.enabled);
  const statusClass = `text-${statusColor({ syncStatus, isConfigured })}`;

  return (
    <div className={classnames('flex items-center gap-1', statusClass)}>
      <StatusIcon
        isConfigured={isConfigured}
        isLoading={isLoading}
        syncStatus={syncStatus}
      />
      <CKSkeleton
        className="h-4 w-[10ch]"
        isLoading={isLoading}
        label="loading"
      >
        <div className="text-sm font-medium">
          {statusText({ syncStatus, isConfigured })}
        </div>
      </CKSkeleton>
    </div>
  );
}

export default IntegrationStatus;
